import { makeAutoObservable } from "mobx";
import type { RootStore } from "./root.store";
import type { TMemberAvailability } from "@/types/availability";
import { getDefaultDateRange } from "@/components/availability/date-helpers";

const MAX_RANGE_DAYS = 90;

function isValidIsoDate(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(new Date(`${value}T00:00:00`).getTime())
  );
}

function diffDays(start: string, end: string): number {
  const s = new Date(`${start}T00:00:00`).getTime();
  const e = new Date(`${end}T00:00:00`).getTime();
  return Math.floor((e - s) / 86_400_000);
}

/**
 * The Availability page's date range and skill filter. The availability
 * itself is read only through Query (`useTeamAvailability`, NG-2814), keyed by
 * the range, so a new range is a new read.
 */
export class WorkspaceAvailabilityStore {
  startDate: string;
  endDate: string;
  selectedSkillIds: Set<string> = new Set();

  constructor(_rootStore: RootStore) {
    const defaults = getDefaultDateRange(13);
    this.startDate = defaults.start;
    this.endDate = defaults.end;
    makeAutoObservable(this, undefined, { autoBind: true });
  }

  /** The members of `members` the skill filter lets through: all of them with none picked. */
  visibleOf(members: TMemberAvailability): TMemberAvailability {
    if (this.selectedSkillIds.size === 0) return members;
    return Object.fromEntries(
      Object.entries(members).filter(([, member]) =>
        (member.skills ?? []).some((id) => this.selectedSkillIds.has(id)),
      ),
    );
  }

  setDateRange(start: string, end: string): { ok: boolean; error?: string } {
    if (!isValidIsoDate(start) || !isValidIsoDate(end)) {
      return { ok: false, error: "invalid_date" };
    }
    const days = diffDays(start, end);
    if (days < 0) return { ok: false, error: "end_before_start" };
    if (days > MAX_RANGE_DAYS) return { ok: false, error: "range_too_long" };
    this.startDate = start;
    this.endDate = end;
    return { ok: true };
  }

  toggleSkillFilter(skillId: string): void {
    if (this.selectedSkillIds.has(skillId)) this.selectedSkillIds.delete(skillId);
    else this.selectedSkillIds.add(skillId);
    this.selectedSkillIds = new Set(this.selectedSkillIds);
  }

  clearSkillFilter(): void {
    this.selectedSkillIds = new Set();
  }
}
